import React from "react";
import { Button, Message } from "semantic-ui-react";
import ValidatorContext from "../../formvalidation/FormContext";
import CompanyInformation from "../components/CompanyInformation";
import { type CompanyInfo, type UserInfo } from "../models";
import UserInformation from "../components/UserInformation";
import { useValidators } from "formvalidation/hooks";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";

interface Props {
  session: server.dto.ClientOnboardingSession
  onFinished: (newSession: server.dto.ClientOnboardingSession) => void
}

const YourDetails = (props: Props) => {
  const [userInformation, setUserInformation] = React.useState<UserInfo>({
    email: props.session.yourDetails.email,
    firstName: props.session.yourDetails.personName.firstName,
    lastName: props.session.yourDetails.personName.lastName,
    lcid: props.session.yourDetails.interfaceCulture.value
  });

  const initialCountry = props.session.yourDetails.companyAddress ? serverReference.countries.find(c => c.code === props.session.yourDetails.companyAddress.countryCode) : undefined;

  const [companyInformation, setCompanyInformation] = React.useState<CompanyInfo>({
    name: props.session.yourDetails.companyName ?? "",
    addressLine1: props.session.yourDetails.companyAddress?.addressLine1 ?? "",
    addressLine2: props.session.yourDetails.companyAddress?.addressLine2 ?? "",
    postalCode: props.session.yourDetails.companyAddress?.postalCode ?? "",
    city: props.session.yourDetails.companyAddress?.city ?? "",
    country: initialCountry,
    state: initialCountry?.states.find(s => props.session.yourDetails.companyAddress ? (s.key === props.session.yourDetails.companyAddress.stateId) : s.isCountryDefault),
    euVatNumber: props.session.yourDetails.vatNumber?.number ?? ""
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [serverErrors, setServerErrors] = React.useState<string[]>([]);

  const validators = useValidators();

  const onSubmit = async (e) => {
    e.preventDefault();

    const validationResult = await validators.validateAll();
    if (validationResult.hasErrors) {
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await window.CoreApi.ClientOnboardingOpen.SetYourDetails({
        companyAddress: {
          addressLine1: companyInformation.addressLine1,
          addressLine2: companyInformation.addressLine2,
          city: companyInformation.city,
          postalCode: companyInformation.postalCode,
          stateId: companyInformation.state.key
        },
        companyName: companyInformation.name,
        vatNumber: companyInformation.euVatNumber,
        firstName: userInformation.firstName,
        lastName: userInformation.lastName,
        lcid: userInformation.lcid,
        secret: props.session.secret,
        email: userInformation.email
      });

      if (res.data.errors.length > 0) {
        setServerErrors(res.data.errors.map(e => e.message));
        return;
      }

      if (props.session.yourDetails.interfaceCulture.value !== userInformation.lcid) {
        window.location.reload();
      } else {
        const newSession = await window.CoreApi.ClientOnboardingOpen.GetOnboardingSession(props.session.secret);
        props.onFinished(newSession.data.data);
      }
    } catch (e) {
      console.error(e);
      setServerErrors([RESX.GeneralLabels.SomethingUnexpectedHappened]);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <ValidatorContext settings={validators.validatorSettings}>
    {/* <Header
            title={"Provide your details"}
        /> */}
    <form onSubmit={onSubmit}>
      <SectionPanel.Section className="gap-huge">

        <SectionPanel.Panel header={<PanelSegment.Title title={"Personal details"} />}>
          <UserInformation
            session={props.session}
            userInfo={userInformation}
            onUserInfoChange={partialUserInfo => { setUserInformation(oldInfo => ({ ...oldInfo, ...partialUserInfo })); }}
          />
        </SectionPanel.Panel>

        <SectionPanel.Panel header={<PanelSegment.Title title={"Company details"} />}>
          <CompanyInformation
            session={props.session}
            companyInfo={companyInformation}
            onCompanyInfoChange={partialCompanyInfo => { setCompanyInformation(oldInfo => ({ ...oldInfo, ...partialCompanyInfo })); }}
          />
        </SectionPanel.Panel>

        <SectionPanel.Panel>
          <div className="flex">
            <div className="grow1 margin-right">
              {serverErrors.length > 0 && <Message color="red">
                {serverErrors.map((error) => <div key={error}>{error}</div>)}
              </Message>}
            </div>
            <div>
              <Button
                primary
                type="submit"
                onClick={onSubmit}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {RESX.Buttons.btnNextStep}
              </Button>
            </div>
          </div>
        </SectionPanel.Panel>
      </SectionPanel.Section>
    </form>
  </ValidatorContext >;
};

export default YourDetails;